import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiResponse, Technician, TechnicianAssignment } from '../../models/modal-system.interfaces';
import { ModalDialogData } from '../../services/carrier-orders-grid/grid-modal-dispatcher.service';

interface TechnicianProvider {
  technician_provider_uuid: string;
  name: string;
  city: string | null;
  region: string | null;
  country: string | null;
}

interface ListAvailableResponse {
  success: boolean;
  data: Technician[];
  providers: TechnicianProvider[];
}

@Component({
  selector: 'app-technician-modal',
  templateUrl: './technician-modal.component.html',
  styleUrls: ['./technician-modal.component.scss'],
})
export class TechnicianModalComponent implements OnInit, OnDestroy {
  readonly API_URL = '/api/dynamic_grids/contact_technician_manager.php';

  assignments: TechnicianAssignment[] = [];
  allTechs: Technician[] = [];
  filteredTechs: Technician[] = [];
  providers: TechnicianProvider[] = [];

  loadingAssignments = false;
  loadingTechs = false;
  saving = false;
  savingProvider = false;

  selectedTechUuid: string | null = null;
  addForm!: FormGroup;
  showProviderForm = false;
  newProviderName = '';

  public techFilterCtrl: FormControl = new FormControl('');
  private _onDestroy = new Subject<void>();
  private assignedTechUuids = new Set<string>();

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<TechnicianModalComponent>,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: ModalDialogData,
  ) {}

  ngOnInit(): void {
    this.addForm = this.fb.group({
      first_name: ['', [Validators.required]],
      last_name: ['', [Validators.required]],
      phone: [''],
      email: [''],
      city: [''],
      region: [''],
      technician_provider_uuid: [null],
    });

    this.loadAssignments();
    this.loadTechs();

    this.techFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterTechsLocally();
      });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected filterTechsLocally(): void {
    let search = this.techFilterCtrl.value;
    const unassigned = this.allTechs.filter(t => !this.assignedTechUuids.has(t.technician_uuid));

    if (!search) {
      this.filteredTechs = [...unassigned];
      return;
    }
    
    search = search.toLowerCase();
    this.filteredTechs = unassigned.filter(t => 
      (t.first_name?.toLowerCase().includes(search) || 
       t.last_name?.toLowerCase().includes(search) ||
       t.email?.toLowerCase().includes(search))
    );
  }

  loadTechs(): void {
    this.loadingTechs = true;
    this.http.post<ListAvailableResponse>(this.API_URL, { action: 'list_available', lading_uuid: this.data.lading_uuid })
      .subscribe({
        next: (r) => {
          this.loadingTechs = false;
          if (!r?.success) return;
          this.allTechs = r.data ?? [];
          if (r.providers) this.providers = r.providers;
          this.filterTechsLocally();
        },
        error: () => this.loadingTechs = false
      });
  }

  loadAssignments(): void {
    this.loadingAssignments = true;
    this.http.post<ApiResponse<TechnicianAssignment[]>>(this.API_URL, { action: 'list_assignments', lading_uuid: this.data.lading_uuid })
      .subscribe({
        next: (r) => {
          this.loadingAssignments = false;
          if (r?.success) {
            this.assignments = r.data ?? [];
            this.rebuildAssignedSet();
            this.filterTechsLocally();
          }
        }
      });
  }

  assignSelected(): void {
    if (!this.selectedTechUuid) return;
    this.saving = true;
    this.http.post<ApiResponse>(this.API_URL, { action: 'assign', lading_uuid: this.data.lading_uuid, technician_uuid: this.selectedTechUuid })
      .subscribe({
        next: (r) => {
          this.saving = false;
          if (!r?.success) { this.toastr.error(r?.message || 'Failed to assign'); return; }
          this.toastr.success('Technician assigned');
          this.selectedTechUuid = null;
          this.loadAssignments();
        }
      });
  }

  updateStatus(a: TechnicianAssignment, status: string): void {
    this.saving = true;
    this.http.post<ApiResponse>(this.API_URL, { 
      action: 'update_status', 
      lading_uuid: this.data.lading_uuid, 
      technician_assignment_uuid: a.technician_assignment_uuid, 
      status 
    }).subscribe({
      next: (r) => {
        this.saving = false;
        if (!r?.success) { 
          this.toastr.error('Failed to update status'); 
          return; 
        }
        a.status = status;
        this.toastr.success('Status updated successfully');
      },
      error: () => {
        this.saving = false;
        this.toastr.error('Network error while updating status');
      }
    });
  }

  unassign(a: TechnicianAssignment): void {
    if (!confirm(`Remove ${this.name(a)}?`)) return;
    this.saving = true;
    this.http.post<ApiResponse>(this.API_URL, { action: 'unassign', lading_uuid: this.data.lading_uuid, technician_assignment_uuid: a.technician_assignment_uuid })
      .subscribe({
        next: (r) => {
          this.saving = false;
          if (!r?.success) { this.toastr.error('Failed to remove'); return; }
          this.toastr.info('Technician removed');
          this.loadAssignments();
        }
      });
  }

  createAndAssign(): void {
    if (this.addForm.invalid) return;
    this.saving = true;
    const v = this.addForm.getRawValue();
    this.http.post<ApiResponse>(this.API_URL, { action: 'create_and_assign', lading_uuid: this.data.lading_uuid, ...v })
      .subscribe({
        next: (r) => {
          this.saving = false;
          if (!r?.success) { this.toastr.error(r?.message || 'Failed to create'); return; }
          this.toastr.success('Technician created and assigned');
          this.addForm.reset();
          this.loadTechs();
          this.loadAssignments();
        }
      });
  }

  toggleProviderForm(): void {
    this.showProviderForm = !this.showProviderForm;
    this.newProviderName = '';
  }

  createProvider(): void {
    const n = this.newProviderName.trim();
    if (!n) return;
    this.savingProvider = true;
    this.http.post<ApiResponse & { technician_provider_uuid?: string; name?: string }>(this.API_URL, { action: 'create_provider', lading_uuid: this.data.lading_uuid, provider_name: n })
      .subscribe({
        next: (r) => {
          this.savingProvider = false;
          if (!r?.success) { this.toastr.error(r?.message || 'Failed to create provider'); return; }
          const np: TechnicianProvider = { technician_provider_uuid: r.technician_provider_uuid!, name: r.name!, city: null, region: null, country: null };
          this.providers = [...this.providers, np].sort((a, b) => a.name.localeCompare(b.name));
          this.addForm.patchValue({ technician_provider_uuid: np.technician_provider_uuid });
          this.showProviderForm = false;
          this.newProviderName = '';
          this.toastr.success(`Provider "${n}" created`);
        }
      });
  }

  private rebuildAssignedSet(): void {
    this.assignedTechUuids.clear();
    this.assignments.forEach(a => { if (a.technician_uuid) this.assignedTechUuids.add(a.technician_uuid); });
  }

  name(a: TechnicianAssignment | Technician | any): string {
    return `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim() || 'Unknown';
  }

  ts(v: string | null): string {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d.getTime()) ? v : d.toLocaleString();
  }

  close(): void { this.dialogRef.close({ refreshGrid: true }); }
  get dialogTitle(): string { return this.data.modalConfig.dialog_title ?? this.data.headerName ?? 'Manage Technicians'; }
}